import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TableSortLabel,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  Security
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { Permission } from '../api/BackofficeApi';

type SortField = 'name' | 'description';
type SortDirection = 'asc' | 'desc';

interface ColumnFilter {
  name: string;
  description: string;
}

interface PermissionInfo {
  name: string;
  description: string;
  category: string;
}

const Permissions: React.FC = () => {
  const [permissions, setPermissions] = useState<PermissionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>({
    name: '',
    description: ''
  });
  
  // Filter menu state
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof ColumnFilter | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Available categories
  const availableCategories = [
    'DRAWING',
    'PROJECT',
    'ELECTRICAL',
    'ADMINISTRATION'
  ];

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      
      // Get all available permissions from the enum
      const permissionValues = Object.values(Permission);
      
      // Map enum values to permission info with descriptions and categories
      const permissionInfos: PermissionInfo[] = permissionValues.map(permission => {
        const permissionName = permission.toString();
        let description = '';
        let category = '';
        
        switch (permissionName) {
          case 'Admin':
            description = 'Full system administration access - can view all organizations and projects';
            category = 'ADMINISTRATION';
            break;
          case 'Backoffice':
            description = 'Access to backoffice administration features';
            category = 'ADMINISTRATION';
            break;
          case 'DWG':
            description = 'Access to DWG drawing functionality';
            category = 'DRAWING';
            break;
          case 'Trapezoids':
            description = 'Access to trapezoid drawing tools';
            category = 'DRAWING';
            break;
          case 'Quantities':
            description = 'Access to quantity calculation features';
            category = 'PROJECT';
            break;
          case 'RealTimeMounting':
            description = 'Access to real-time mounting features';
            category = 'PROJECT';
            break;
          case 'Dimensions':
            description = 'Access to dimensioning tools';
            category = 'DRAWING';
            break;
          case 'Holes':
            description = 'Access to hole creation and management';
            category = 'DRAWING';
            break;
          case 'Tags':
            description = 'Access to tagging functionality';
            category = 'PROJECT';
            break;
          case 'ProjectSave':
            description = 'Permission to save projects';
            category = 'PROJECT';
            break;
          case 'ProjectLoad':
            description = 'Permission to load projects';
            category = 'PROJECT';
            break;
          case 'Electric':
            description = 'Access to electrical design features';
            category = 'ELECTRICAL';
            break;
          default:
            description = 'System permission';
            category = 'SYSTEM';
        }
        
        return {
          name: permissionName,
          description,
          category
        };
      });
      
      setPermissions(permissionInfos);
    } catch (err: any) {
      setError('Failed to load permissions');
      console.error('Permissions error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const sortPermissions = (permissions: PermissionInfo[]) => {
    return [...permissions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'description':
          aValue = a.description || '';
          bValue = b.description || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filtering function
  const filterPermissions = (permissions: PermissionInfo[]) => {
    return permissions.filter(permission => {
      const matchesSearch = !searchTerm || 
        permission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.category?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesNameFilter = !columnFilters.name || 
        permission.name?.toLowerCase().includes(columnFilters.name.toLowerCase());

      const matchesDescriptionFilter = !columnFilters.description || 
        permission.description?.toLowerCase().includes(columnFilters.description.toLowerCase());

      return matchesSearch && matchesNameFilter && matchesDescriptionFilter;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };


  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: keyof ColumnFilter) => {
    setFilterMenuAnchor(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterChange = (value: string) => {
    if (activeFilterColumn) {
      setColumnFilters(prev => ({
        ...prev,
        [activeFilterColumn]: value
      }));
    }
    setFilterMenuAnchor(null);
    setActiveFilterColumn(null);
  };

  const clearFilters = () => {
    setColumnFilters({
      name: '',
      description: ''
    });
    setSearchTerm('');
  };

  const filteredAndSortedPermissions = sortPermissions(filterPermissions(permissions));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          System Permissions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Available system permissions (read-only)
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={(e) => handleFilterClick(e, 'name')}
                >
                  Name
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={(e) => handleFilterClick(e, 'description')}
                >
                  Description
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortDirection : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>Category</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedPermissions.map((permission, index) => (
              <TableRow key={permission.name} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Security color="primary" />
                    <Typography variant="body2" fontWeight="medium">
                      {permission.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {permission.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={permission.category}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {/* Column Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem>
          <TextField
            autoFocus
            placeholder={`Filter by ${activeFilterColumn}...`}
            value={activeFilterColumn ? columnFilters[activeFilterColumn] : ''}
            onChange={(e) => handleFilterChange(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Permissions;
